import React, { FC } from 'react';
import { Box, Typography, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  Legend,
  Brush,
} from 'recharts';
import { formatCost } from '@/components/pages/kiosk/monitor/StaffMonitor/functions';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import InTestingComponent from '@/components/InTestingComponent';

interface CostDataPoint {
  time: string;
  cost: number;
  hours: number;
}

type Aggregation = 'hourly' | 'daily' | 'weekly' | 'monthly';
type ChartMode = 'cost' | 'hours';

interface CostTrendChartProps {
  title: string;
  totalValue: number;
  costOverTimeData: CostDataPoint[];
  aggregation: Aggregation;
  onAggregationChange: (aggregation: Aggregation) => void;
  chartMode: ChartMode;
  onChartModeChange: (mode: ChartMode) => void;
  className?: string;
}

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {},
  totalCost: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.success.main,
  },
  controlsSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
  },
  controlsLabel: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: 4,
  },
  aggButton: {
    minWidth: 'auto',
    padding: '6px 12px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  aggButtonActive: {
    backgroundColor: '#00A3FF !important', // Blue active state
    color: '#FFFFFF !important',
    '&:hover': {
      backgroundColor: '#3C6FD1 !important',
    },
  },
  chartWrapper: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    '& .recharts-cartesian-axis-tick text': {
      fontSize: 11,
      fill: '#9CA3AF',
    },
  },
  tooltipPaper: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: theme.spacing(1.5),
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  tooltipLabel: {},
  tooltipValue: {
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 14,
  },
  tooltipMetricLabel: {
    fontSize: 12,
    color: '#32D483',
  },
  modeToggleGroup: {
    marginRight: 16,
  },
  controlsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
}));

const formatHours = (hours: number): string => {
  return `${hours.toFixed(1)} hrs`;
};

interface CustomTooltipLineProps extends TooltipProps<number, string> {
  chartMode: ChartMode;
}

const CustomTooltipLine: FC<CustomTooltipLineProps> = ({ active, payload, label, chartMode }) => {
  const classes = useStyles();
  if (active && payload && payload.length) {
    const value = payload[0]?.value;
    const isCostMode = chartMode === 'cost';
    return (
      <Paper className={classes.tooltipPaper}>
        <Box>
          <Typography className={classes.tooltipLabel}>{label}</Typography>
          <Typography className={classes.tooltipValue}>
            {isCostMode ? formatCost(Number(value)) : formatHours(Number(value))}
          </Typography>
          <Typography className={classes.tooltipMetricLabel}>{isCostMode ? 'Cost' : 'Hours'}</Typography>
        </Box>
      </Paper>
    );
  }
  return null;
};

const CostTrendChart: React.FC<CostTrendChartProps> = ({
  title,
  totalValue,
  costOverTimeData,
  aggregation,
  onAggregationChange,
  chartMode,
  onChartModeChange,
  className,
}) => {
  const classes = useStyles();
  const aggregationOptions: Aggregation[] = ['hourly', 'daily', 'weekly', 'monthly'];
  const isCostMode = chartMode === 'cost';

  const dataKey = isCostMode ? 'cost' : 'hours';
  const lineName = isCostMode ? 'Cost' : 'Hours';
  const lineColor = isCostMode ? '#32D483' : '#00A3FF';

  const yAxisFormatter = isCostMode
    ? (value: number) => `$${(value / 1000).toFixed(0)}k`
    : (value: number) => `${value.toFixed(0)}h`;

  const displayTotal = isCostMode ? formatCost(totalValue) : formatHours(totalValue);

  return (
    <Box className={`${classes.card} ${className || ''}`}>
      <Box className={classes.header}>
        <Box className={classes.titleSection}>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          <Typography className={classes.totalCost}>{displayTotal}</Typography>
        </Box>

        <Box className={classes.controlsSection}>
          <Box className={classes.controlsRow}>
            {/* Hours/Cost Toggle - Feature flagged */}
            <InTestingComponent>
              <ToggleButtonGroup className={classes.modeToggleGroup}>
                <ToggleButton
                  value="cost"
                  className={`${classes.aggButton} ${chartMode === 'cost' ? classes.aggButtonActive : ''}`}
                  onClick={() => onChartModeChange('cost')}
                >
                  Cost
                </ToggleButton>
                <ToggleButton
                  value="hours"
                  className={`${classes.aggButton} ${chartMode === 'hours' ? classes.aggButtonActive : ''}`}
                  onClick={() => onChartModeChange('hours')}
                >
                  Hours
                </ToggleButton>
              </ToggleButtonGroup>
            </InTestingComponent>

            {/* Aggregation Toggle */}
            <ToggleButtonGroup>
              {aggregationOptions.map(option => (
                <ToggleButton
                  key={option}
                  value={option}
                  className={`${classes.aggButton} ${aggregation === option ? classes.aggButtonActive : ''}`}
                  onClick={() => onAggregationChange(option)}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

        </Box>
      </Box>

      <Box className={classes.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={costOverTimeData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#0b245f" vertical={false} />
            <XAxis dataKey="time" stroke="#9CA3AF" tickLine={false} axisLine={false} dy={10} />
            <YAxis stroke="#9CA3AF" tickFormatter={yAxisFormatter} tickLine={false} axisLine={false} />
            <Tooltip
              content={<CustomTooltipLine chartMode={chartMode} />}
              cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey={dataKey}
              name={lineName}
              stroke={lineColor}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }}
            />
            <Brush dataKey="time" height={20} stroke="#3C6FD1" fill="#020B26" travellerWidth={10} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default CostTrendChart;
